// TSL Status Counter Component
// Real-time status tracker showing TSL metrics and performance

import type { Position } from '../types/mexc';
import { Shield, Activity, AlertTriangle, Clock, TrendingUp, Zap } from 'lucide-react';
import { useGlobalTSLStatus } from '../hooks/useTSLStatus';

interface TSLStatusCounterProps {
  positions: Position[];
  className?: string;
}

export function TSLStatusCounter({ positions, className = '' }: TSLStatusCounterProps) {
  const globalStatus = useGlobalTSLStatus(positions);

  // Helper function to format time ago
  const formatTimeAgo = (timestamp?: number) => {
    if (!timestamp) return 'Never';
    
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Helper function to format processing time
  const formatProcessingTime = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  // Determine overall health status
  const getOverallHealthStatus = () => {
    if (!globalStatus.engineStatus.isRunning) {
      return { status: 'error', message: 'Engine stopped' };
    }
    if (globalStatus.recentErrors > 0) {
      return { status: 'warning', message: `${globalStatus.recentErrors} recent errors` };
    }
    if (globalStatus.totalActivePositions === 0) {
      return { status: 'inactive', message: 'No active TSL positions' };
    }
    return { status: 'healthy', message: 'All systems operational' };
  };

  const healthStatus = getOverallHealthStatus();

  // Status indicator colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'error':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'inactive':
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            TSL Status
          </h3>
        </div>
        
        {/* Overall health indicator */}
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getStatusColor(healthStatus.status)}`}>
          {healthStatus.status === 'healthy' && <Activity className="w-4 h-4" />}
          {healthStatus.status === 'warning' && <AlertTriangle className="w-4 h-4" />}
          {healthStatus.status === 'error' && <AlertTriangle className="w-4 h-4" />}
          {healthStatus.status === 'inactive' && <Shield className="w-4 h-4" />}
          <span className="text-sm font-medium">{healthStatus.message}</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Active Positions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-2 mb-1">
            <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Active</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {globalStatus.totalActivePositions}
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-400">
            of {globalStatus.totalEnabledPositions} enabled
          </div>
        </div>

        {/* Recent Adjustments */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
          <div className="flex items-center space-x-2 mb-1">
            <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">Adjustments</span>
          </div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">
            {globalStatus.recentAdjustments}
          </div>
          <div className="text-xs text-green-600 dark:text-green-400">
            last hour
          </div>
        </div>

        {/* Recent Errors */}
        <div className={`rounded-lg p-3 border ${
          globalStatus.recentErrors > 0 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
        }`}>
          <div className="flex items-center space-x-2 mb-1">
            <AlertTriangle className={`w-4 h-4 ${
              globalStatus.recentErrors > 0 
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-600 dark:text-gray-400'
            }`} />
            <span className={`text-sm font-medium ${
              globalStatus.recentErrors > 0 
                ? 'text-red-700 dark:text-red-300'
                : 'text-gray-700 dark:text-gray-300'
            }`}>
              Errors
            </span>
          </div>
          <div className={`text-2xl font-bold ${
            globalStatus.recentErrors > 0 
              ? 'text-red-900 dark:text-red-100'
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            {globalStatus.recentErrors}
          </div>
          <div className={`text-xs ${
            globalStatus.recentErrors > 0 
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}>
            last hour
          </div>
        </div>

        {/* Performance */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center space-x-2 mb-1">
            <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Performance</span>
          </div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100">
            {formatProcessingTime(globalStatus.performanceMetrics.averageProcessingTime)}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400">
            avg processing
          </div>
        </div>
      </div>

      {/* Engine Status Details */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                globalStatus.engineStatus.isRunning 
                  ? 'bg-green-500 animate-pulse' 
                  : 'bg-red-500'
              }`} />
              <span className="text-gray-600 dark:text-gray-400">
                Engine {globalStatus.engineStatus.isRunning ? 'Running' : 'Stopped'}
              </span>
            </div>
            
            {globalStatus.engineStatus.lastProcessTime && (
              <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                <Clock className="w-3 h-3" />
                <span>
                  Last cycle: {formatTimeAgo(globalStatus.engineStatus.lastProcessTime)}
                </span>
              </div>
            )}
          </div>
          
          {globalStatus.performanceMetrics.lastActivityTime && (
            <div className="text-gray-500 dark:text-gray-400">
              Last activity: {formatTimeAgo(globalStatus.performanceMetrics.lastActivityTime)}
            </div>
          )}
        </div>
        
        {/* Next processing time */}
        {globalStatus.engineStatus.nextProcessTime && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Next cycle: {formatTimeAgo(globalStatus.engineStatus.nextProcessTime)}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact version for smaller spaces
export function TSLStatusCounterCompact({ positions, className = '' }: TSLStatusCounterProps) {
  const globalStatus = useGlobalTSLStatus(positions);

  const getStatusIcon = () => {
    if (!globalStatus.engineStatus.isRunning) {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    }
    if (globalStatus.recentErrors > 0) {
      return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    }
    if (globalStatus.totalActivePositions === 0) {
      return <Shield className="w-4 h-4 text-gray-500" />;
    }
    return <Activity className="w-4 h-4 text-green-500" />;
  };

  return (
    <div className={`flex items-center space-x-4 px-3 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Status Icon */}
      <div className="flex items-center space-x-2">
        {getStatusIcon()}
        <span className="text-sm font-medium text-gray-900 dark:text-white">TSL</span>
      </div>
      
      {/* Active Count */}
      <div className="flex items-center space-x-1">
        <span className="text-sm text-gray-600 dark:text-gray-400">Active:</span>
        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
          {globalStatus.totalActivePositions}
        </span>
      </div>
      
      {/* Recent Activity */}
      {globalStatus.recentAdjustments > 0 && (
        <div className="flex items-center space-x-1">
          <TrendingUp className="w-3 h-3 text-green-500" />
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
            {globalStatus.recentAdjustments}
          </span>
        </div>
      )}
      
      {/* Errors */}
      {globalStatus.recentErrors > 0 && (
        <div className="flex items-center space-x-1">
          <AlertTriangle className="w-3 h-3 text-red-500" />
          <span className="text-sm font-semibold text-red-600 dark:text-red-400">
            {globalStatus.recentErrors}
          </span>
        </div>
      )}
    </div>
  );
}
